// components/employee/MySalesComponent.js
import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Calendar, Filter, Download, Eye } from 'lucide-react';
import employeeService from '../../services/employeeService';

const MySalesComponent = () => {
  const [mySales, setMySales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    limit: 20
  });

  useEffect(() => {
    loadMySales();
  }, []);

  const loadMySales = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await employeeService.getMySales(
        filters.startDate || null,
        filters.endDate || null,
        filters.limit
      );
      
      if (response.success) {
        setMySales(response.data);
      } else {
        setError(response.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    loadMySales();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' F CFA';
  };

  const getStatusBadge = (sale) => {
    if (sale.isCredit) {
      return sale.isCreditPaid ? 
        <Badge bg="success">Crédit soldé</Badge> : 
        <Badge bg="warning">Crédit actif</Badge>;
    }
    return <Badge bg="success">Payé</Badge>;
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Mes Ventes</h5>
        <Button variant="outline-primary" size="sm">
          <Download size={16} className="me-2" />
          Exporter
        </Button>
      </Card.Header>
      
      <Card.Body>
        {/* Filtres */}
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Date début</Form.Label>
              <Form.Control
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Date fin</Form.Label>
              <Form.Control
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Limite</Form.Label>
              <Form.Select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button variant="primary" onClick={applyFilters} disabled={loading}>
              <Filter size={16} className="me-2" />
              Filtrer
            </Button>
          </Col>
        </Row>

        {/* Contenu */}
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Chargement de mes ventes...</p>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <small className="text-muted">
                {mySales.length} vente{mySales.length > 1 ? 's' : ''} trouvée{mySales.length > 1 ? 's' : ''}
              </small>
            </div>
            
            <Table responsive hover>
              <thead className="table-light">
                <tr>
                  <th>N° Vente</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Articles</th>
                  <th>Total</th>
                  <th>Paiement</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mySales.map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      <code className="text-primary">{sale.saleNumber}</code>
                    </td>
                    <td>
                      <div>
                        <div>{new Date(sale.createdAt).toLocaleDateString('fr-FR')}</div>
                        <small className="text-muted">
                          {new Date(sale.createdAt).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </small>
                      </div>
                    </td>
                    <td>{sale.customerName}</td>
                    <td>
                      <Badge bg="secondary" pill>
                        {sale.items?.length || sale.totalQuantity} articles
                      </Badge>
                    </td>
                    <td className="fw-bold text-success">
                      {formatCurrency(sale.total)}
                    </td>
                    <td>
                      <Badge bg="info">
                        {sale.paymentMethod === 'CASH' ? 'Espèces' :
                         sale.paymentMethod === 'WAVE' ? 'Wave' :
                         sale.paymentMethod === 'ORANGE_MONEY' ? 'Orange Money' :
                         sale.paymentMethod}
                      </Badge>
                    </td>
                    <td>{getStatusBadge(sale)}</td>
                    <td>
                      <Button variant="outline-primary" size="sm">
                        <Eye size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {mySales.length === 0 && (
              <div className="text-center py-4">
                <Calendar size={48} className="text-muted mb-3" />
                <p className="text-muted">Aucune vente trouvée pour cette période</p>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default MySalesComponent;